import {
  collection,
  orderBy,
  query,
  where,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
  type CollectionReference,
} from 'firebase/firestore'
import { getDocsWithError } from '../../firestore-mobx/fetch'
import { convertDocumentSnapshotToModel } from '../../firestore-mobx/stream'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import type { FirestoreRoomStateEvent } from './schema'
import { RoomStateEvent } from '../../models/RoomStateEvent'
import { createBatchGet } from '../../util/batch-get'

const converter: FirestoreDataConverter<FirestoreRoomStateEvent> = {
  toFirestore: (data) => data,
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    const eventTime = data.eventTime.toDate()
    return {
      ...data,
      eventTime,
    } as FirestoreRoomStateEvent
  },
}

const getColRef = (
  firestore: Firestore,
  roomId: string
): CollectionReference<FirestoreRoomStateEvent> => {
  return collection(firestore, 'room_state', roomId, 'events').withConverter(
    converter
  )
}

export const fetchAllRoomStateEventsForRoom = async (
  repository: FirebaseRepository,
  { roomId }: { roomId: string }
) => {
  if (!roomId) return []

  const colRef = getColRef(repository.firestore, roomId)
  const snapshot = await getDocsWithError(
    colRef,
    'FetchAllRoomStateEventsError'
  )

  return snapshot.docs.map((doc) =>
    convertDocumentSnapshotToModel(repository, doc, RoomStateEvent)
  )
}

// Transcript events use eventType 2. The transcript dialog only renders these,
// so query for them directly instead of reading the whole events collection.
const EVENT_TYPE_TRANSCRIPT = 2

export const fetchTranscriptEventsForRoom = async (
  repository: FirebaseRepository,
  { roomId }: { roomId: string }
) => {
  if (!roomId) return []

  const colRef = getColRef(repository.firestore, roomId)
  const transcriptQuery = query(
    colRef,
    where('eventType', '==', EVENT_TYPE_TRANSCRIPT),
    orderBy('eventTime', 'asc')
  )
  const snapshot = await getDocsWithError(
    transcriptQuery,
    'FetchTranscriptEventsError'
  )

  return snapshot.docs.map((doc) =>
    convertDocumentSnapshotToModel(repository, doc, RoomStateEvent)
  )
}

export const fetchRoomStateEvents = async (
  repository: FirebaseRepository,
  { roomId, eventIds }: { roomId: string; eventIds: string[] }
) => {
  // roomId can arrive as an empty string from certain call sites, which would
  // produce the invalid path "room_state/events" (2 segments) and throw a
  // FirebaseError. Return an empty result (no events) early instead.
  if (!roomId) return []

  const colRef = getColRef(repository.firestore, roomId)

  return createBatchGet({
    repository,
    collectionRef: colRef,
    ids: eventIds,
    modelClass: RoomStateEvent,
  })
}
